import { z } from 'zod';
import { D } from '../decimal.js';
import { DecimalStringSchema, IsoDateSchema } from './primitives.js';

const PercentageSchema = DecimalStringSchema.refine(
  (value) => D(value).gte(0) && D(value).lte(100),
  'Must be between 0 and 100',
);
const NonNegativeDecimalSchema = DecimalStringSchema.refine((value) => D(value).gte(0), {
  message: 'Must be non-negative',
});

export const InvestmentStrategyBucketSchema = z.enum(['BTC', 'ETH', 'RESERVE', 'OTHER']);
export const InvestmentStrategyRiskClassSchema = z.enum([
  'NATIVE',
  'WRAPPED',
  'CENTRALIZED_STABLE',
  'DECENTRALIZED_STABLE',
  'YIELD_STABLE',
  'OTHER',
]);

export const InvestmentStrategyAssetRuleSchema = z.object({
  assetKey: z.string().min(3),
  bucket: InvestmentStrategyBucketSchema,
  riskClass: InvestmentStrategyRiskClassSchema,
  stressLossPct: PercentageSchema,
});

export type InvestmentStrategyAssetRule = Readonly<
  z.infer<typeof InvestmentStrategyAssetRuleSchema>
>;

export const InvestmentStrategyConfigSchema = z
  .object({
    availableCapitalEur: NonNegativeDecimalSchema,
    liquidTreasuryEur: NonNegativeDecimalSchema,
    liquidityFloorEur: NonNegativeDecimalSchema,
    knownCashNeedsEur: NonNegativeDecimalSchema,
    taxReserveEur: NonNegativeDecimalSchema,
    maxStressLossEur: NonNegativeDecimalSchema,
    policyConfirmed: z.boolean(),
    tacticalOverlayEnabled: z.boolean(),
    deploymentMonths: z.number().int().min(1).max(60),
    regularDeploymentPct: PercentageSchema,
    tacticalTranches: z.number().int().min(1).max(12),
    minimumOrderEur: NonNegativeDecimalSchema,
    maxPriceAgeHours: z.number().int().min(1).max(168),
    targetBtcPct: PercentageSchema,
    targetEthPct: PercentageSchema,
    targetReservePct: PercentageSchema,
    rebalanceTolerancePct: PercentageSchema,
    btcStressLossPct: PercentageSchema,
    ethStressLossPct: PercentageSchema,
    salesPolicy: z.enum(['CASH_FLOW_ONLY', 'HARD_BANDS']),
    checkonchainMetricId: z.string().min(1).nullable(),
    checkonchainMetricVersion: z.string().min(1).nullable(),
    checkonchainQuantile: PercentageSchema.nullable(),
    checkonchainObservedOn: IsoDateSchema.nullable(),
    assetRules: z.array(InvestmentStrategyAssetRuleSchema).readonly(),
  })
  .superRefine((config, context) => {
    const targetTotal = D(config.targetBtcPct)
      .plus(config.targetEthPct)
      .plus(config.targetReservePct);
    if (!targetTotal.equals(100)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['targetReservePct'],
        message: 'Target allocations must total 100',
      });
    }

    const manualFields = [
      config.checkonchainMetricId,
      config.checkonchainMetricVersion,
      config.checkonchainQuantile,
      config.checkonchainObservedOn,
    ];
    const manualFieldCount = manualFields.filter((value) => value !== null).length;
    if (manualFieldCount !== 0 && manualFieldCount !== manualFields.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['checkonchainMetricId'],
        message: 'Manual metric identity, version, quantile and date must be set together',
      });
    }

    const assetKeys = new Set<string>();
    for (const [index, rule] of config.assetRules.entries()) {
      const normalized = rule.assetKey.trim().toLowerCase();
      if (assetKeys.has(normalized)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['assetRules', index, 'assetKey'],
          message: 'Asset rules must have unique portable identities',
        });
      }
      assetKeys.add(normalized);
    }
  });

export type InvestmentStrategyConfig = Readonly<z.infer<typeof InvestmentStrategyConfigSchema>>;

export const DEFAULT_INVESTMENT_STRATEGY_CONFIG: InvestmentStrategyConfig = {
  availableCapitalEur: '30000',
  liquidTreasuryEur: '30000',
  liquidityFloorEur: '0',
  knownCashNeedsEur: '0',
  taxReserveEur: '0',
  maxStressLossEur: '30000',
  policyConfirmed: false,
  tacticalOverlayEnabled: false,
  deploymentMonths: 12,
  regularDeploymentPct: '60',
  tacticalTranches: 4,
  minimumOrderEur: '100',
  maxPriceAgeHours: 24,
  targetBtcPct: '50',
  targetEthPct: '35',
  targetReservePct: '15',
  rebalanceTolerancePct: '5',
  btcStressLossPct: '80',
  ethStressLossPct: '85',
  salesPolicy: 'CASH_FLOW_ONLY',
  checkonchainMetricId: null,
  checkonchainMetricVersion: null,
  checkonchainQuantile: null,
  checkonchainObservedOn: null,
  assetRules: [],
};

export const InvestmentStrategySignalSchema = z.object({
  available: z.boolean(),
  source: z.enum(['ALAFEC_200D', 'CHECKONCHAIN_MANUAL', 'ALLOCATION_ONLY']),
  zone: z.enum(['INCREASED', 'REGULAR', 'REDUCED', 'UNAVAILABLE']),
  trend: z.enum(['RISING', 'STABLE', 'FALLING', 'UNAVAILABLE']),
  valuation: z.enum(['CHEAP', 'NEUTRAL', 'EXPENSIVE', 'UNAVAILABLE']),
  multiplier: DecimalStringSchema,
  observedOn: IsoDateSchema.nullable(),
  currentPriceEur: DecimalStringSchema.nullable(),
  average200dEur: DecimalStringSchema.nullable(),
  average200dSlopePct: DecimalStringSchema.nullable(),
  mayerMultiple: DecimalStringSchema.nullable(),
  metricId: z.string().nullable(),
  metricVersion: z.string().nullable(),
  checkonchainQuantile: DecimalStringSchema.nullable(),
  sampleSize: z.number().int().nonnegative(),
});

export const InvestmentStrategyAllocationSchema = z.object({
  bucket: InvestmentStrategyBucketSchema,
  symbols: z.array(z.string()).readonly(),
  currentValueEur: DecimalStringSchema,
  currentPct: DecimalStringSchema,
  targetPct: DecimalStringSchema,
  targetValueEur: DecimalStringSchema,
  gapEur: DecimalStringSchema,
  lowerBandPct: DecimalStringSchema,
  upperBandPct: DecimalStringSchema,
  recommendedAction: z.enum(['BUY', 'HOLD', 'SELL', 'RESERVE']),
  recommendedAmountEur: DecimalStringSchema,
});

export const InvestmentStrategyExposureSchema = z.object({
  assetKey: z.string(),
  symbol: z.string(),
  currentValueEur: DecimalStringSchema,
  valued: z.boolean(),
  priceObservedAt: z.number().int().nonnegative().nullable(),
  priceAgeHours: DecimalStringSchema.nullable(),
  classificationStatus: z.enum(['CLASSIFIED', 'UNCLASSIFIED']),
  bucket: InvestmentStrategyBucketSchema.nullable(),
  riskClass: InvestmentStrategyRiskClassSchema.nullable(),
  stressLossPct: DecimalStringSchema.nullable(),
  stressLossEur: DecimalStringSchema,
});

export const InvestmentStrategyReadinessReasonSchema = z.object({
  code: z.enum([
    'POLICY_UNCONFIRMED',
    'POSITION_RECONSTRUCTION_FAILED',
    'UNVALUED_POSITION',
    'STALE_PRICE',
    'UNCLASSIFIED_ASSET',
  ]),
  message: z.string(),
});

export const InvestmentStrategyReadinessSchema = z.object({
  status: z.enum(['READY', 'BLOCKED']),
  reasons: z.array(InvestmentStrategyReadinessReasonSchema).readonly(),
  valuationCoveragePct: DecimalStringSchema,
  unvaluedPositionCount: z.number().int().nonnegative(),
  stalePositionCount: z.number().int().nonnegative(),
  unclassifiedPositionCount: z.number().int().nonnegative(),
});

export const InvestmentStrategySummarySchema = z.object({
  currentPortfolioEur: DecimalStringSchema,
  plannedPortfolioEur: DecimalStringSchema,
  availableCapitalEur: DecimalStringSchema,
  liquidTreasuryEur: DecimalStringSchema,
  treasuryHeadroomEur: DecimalStringSchema,
  investableCapitalEur: DecimalStringSchema,
  regularMonthlyEur: DecimalStringSchema,
  tacticalTrancheEur: DecimalStringSchema,
  plannedTrancheEur: DecimalStringSchema,
  cryptoBuyEur: DecimalStringSchema,
  keptInReserveEur: DecimalStringSchema,
  sellEur: DecimalStringSchema,
  currentStressLossEur: DecimalStringSchema,
  postDecisionStressLossEur: DecimalStringSchema,
  maxStressLossEur: DecimalStringSchema,
  riskHeadroomEur: DecimalStringSchema,
  riskLimitedEur: DecimalStringSchema,
  action: z.enum(['BUY', 'HOLD', 'SELL', 'BLOCKED']),
});

export const InvestmentStrategyDecisionSchema = z.object({
  id: z.string().nullable(),
  windowKey: z.string().regex(/^\d{4}-\d{2}$/),
  validFrom: IsoDateSchema,
  validUntil: IsoDateSchema,
  status: z.enum(['BLOCKED', 'NOT_REQUIRED', 'PREVIEW', 'PROPOSED', 'PARTIAL', 'EXECUTED']),
  recommendedEur: DecimalStringSchema,
  executedEur: DecimalStringSchema,
  remainingEur: DecimalStringSchema,
});

export const InvestmentStrategyResponseSchema = z.object({
  asOfDate: IsoDateSchema,
  config: InvestmentStrategyConfigSchema,
  readiness: InvestmentStrategyReadinessSchema,
  signal: InvestmentStrategySignalSchema,
  summary: InvestmentStrategySummarySchema,
  decision: InvestmentStrategyDecisionSchema,
  allocations: z.array(InvestmentStrategyAllocationSchema).readonly(),
  exposures: z.array(InvestmentStrategyExposureSchema).readonly(),
  warnings: z.array(z.string()).readonly(),
});

export const InvestmentStrategyConfigResponseSchema = z.object({
  config: InvestmentStrategyConfigSchema,
});

export const InvestmentStrategyExecutionInputSchema = z.object({
  amountEur: NonNegativeDecimalSchema.refine((value) => D(value).gt(0), {
    message: 'Must be positive',
  }),
  txHash: z.string().min(1).nullable(),
  note: z.string().max(500).nullable(),
});

export const InvestmentStrategyExecutionResponseSchema = z.object({
  decision: InvestmentStrategyDecisionSchema,
});

export type InvestmentStrategySignal = Readonly<z.infer<typeof InvestmentStrategySignalSchema>>;
export type InvestmentStrategyAllocation = Readonly<
  z.infer<typeof InvestmentStrategyAllocationSchema>
>;
export type InvestmentStrategyExposure = Readonly<z.infer<typeof InvestmentStrategyExposureSchema>>;
export type InvestmentStrategyReadiness = Readonly<
  z.infer<typeof InvestmentStrategyReadinessSchema>
>;
export type InvestmentStrategySummary = Readonly<z.infer<typeof InvestmentStrategySummarySchema>>;
export type InvestmentStrategyDecision = Readonly<z.infer<typeof InvestmentStrategyDecisionSchema>>;
export type InvestmentStrategyResponse = Readonly<z.infer<typeof InvestmentStrategyResponseSchema>>;
export type InvestmentStrategyConfigResponse = Readonly<
  z.infer<typeof InvestmentStrategyConfigResponseSchema>
>;
export type InvestmentStrategyExecutionInput = Readonly<
  z.infer<typeof InvestmentStrategyExecutionInputSchema>
>;
export type InvestmentStrategyExecutionResponse = Readonly<
  z.infer<typeof InvestmentStrategyExecutionResponseSchema>
>;
